import { capitalize } from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, IconTooltip, ModalUI, showNotification } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import ScoringInfoModal from '../ScoringInfoModal/ScoringInfoModal';

import type { MutableRefObject } from 'react';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type SonataMORETooltipProps = {
  /** The currently selected optimization model full object (from the API). */
  optimizationModel: Record<string, any>;
  user: Record<string, any>;
  userRoles: UserRoleInterface[];
};

type State = {
  scoringInfoModalRef: MutableRefObject<any>;
};

/**
 * Renders the ℹ IconTooltip with model metadata and the optional
 * "Scoring Info" button that opens the QualityScoring modal.
 */
function SonataMORETooltip({ optimizationModel, user, userRoles }: SonataMORETooltipProps) {
  const { formatMessage } = useIntl();

  const [{ scoringInfoModalRef }] = useState<State>({
    scoringInfoModalRef: useRef(null)
  });

  const selectedModelCountries = useMemo(() => {
    return (optimizationModel.countries as string[])
      .map(countryCode => {
        const countryFound = worldCountries.find(
          (wCountry: Record<string, any>) => wCountry.cca3 === countryCode.toUpperCase()
        );
        return countryFound ? capitalize(countryFound.name.common) : countryCode.toUpperCase();
      })
      .join(', ');
  }, [optimizationModel]);

  const selectedModelCategories = useMemo(() => {
    const { categories } = optimizationModel;
    if (categories) {
      return (categories as string[])
        .map(category =>
          formatMessage({
            defaultMessage: 'N/A',
            id: `audienceVariables.${category.toUpperCase()}`
          })
        )
        .join(', ');
    }
    return formatMessage({
      defaultMessage: 'N/A',
      id: 'optimizationModel.unknownCategories'
    });
  }, [optimizationModel, formatMessage]);

  const selectedModelFeatures = useMemo(() => {
    const { datasource } = optimizationModel;
    const { features } = datasource;
    if (features) {
      return (features as string[])
        .map(feature => {
          const cpy = formatMessage({
            defaultMessage: capitalize(feature),
            id: `optimizationModel.features.${feature}`
          });
          return cpy === `optimizationModel.features.${feature}`
            ? formatMessage({
                defaultMessage: 'N/A',
                id: 'optimizationModel.features.unknown'
              })
            : cpy;
        })
        .join(', ');
    }
    return formatMessage({
      defaultMessage: 'N/A',
      id: 'optimizationModel.features.unknown'
    });
  }, [optimizationModel, formatMessage]);

  const openScoringInfoModal = useCallback(() => {
    const featuresQualityScoring = optimizationModel.qualityScoring?.featuresQualityScoring;
    if (!featuresQualityScoring || featuresQualityScoring.length === 0) {
      showNotification(
        'warning',
        formatMessage({
          defaultMessage: 'Selected optimization model scoring info is not available',
          id: 'optimizationModel.infoModal.warningSelectedOptimizationModel'
        })
      );
      return;
    }
    scoringInfoModalRef?.current?.openModal();
  }, [optimizationModel, scoringInfoModalRef, formatMessage]);

  const closeScoringInfoModal = useCallback(() => {
    scoringInfoModalRef?.current?.closeModal();
  }, [scoringInfoModalRef]);

  const canViewQualityInfo = checkUserResourcePermissions('campaignOptimizationQualityInfo', user, userRoles);

  return (
    <div className="sonataMORETooltip">
      <IconTooltip customClassName="sonataMORETooltip__icon" icon="fas fa-info-circle" place="right">
        <div className="sonataMORETooltip__header">
          {formatMessage({
            defaultMessage: 'Selected Optimization Model Info',
            id: 'optimizationModel.campaignOptimizationModelTooltipHeader'
          })}
        </div>
        <div className="sonataMORETooltip__field">
          <div className="sonataMORETooltip__fieldName">
            {formatMessage({
              defaultMessage: 'KPI',
              id: 'optimizationModel.kpi.kpi'
            })}
          </div>
          <div className="sonataMORETooltip__fieldValue">
            {formatMessage({
              defaultMessage: capitalize(optimizationModel.kpi),
              id: `optimizationModel.kpi.${optimizationModel.kpi}`
            })}
          </div>
        </div>
        <div className="sonataMORETooltip__field">
          <div className="sonataMORETooltip__fieldName">
            {formatMessage({
              defaultMessage: 'Training since',
              id: 'optimizationModel.timeFrame.from'
            })}
          </div>
          <div className="sonataMORETooltip__fieldValue">
            {optimizationModel.datasource?.dateRange?.from
              ? moment(optimizationModel.datasource.dateRange.from).format('YYYY-MM-DD')
              : 'N/A'}
          </div>
        </div>
        <div className="sonataMORETooltip__field">
          <div className="sonataMORETooltip__fieldName">
            {formatMessage({
              defaultMessage: 'Training to',
              id: 'optimizationModel.timeFrame.to'
            })}
          </div>
          <div className="sonataMORETooltip__fieldValue">
            {optimizationModel.datasource?.dateRange?.to
              ? moment(optimizationModel.datasource.dateRange.to).format('YYYY-MM-DD')
              : 'N/A'}
          </div>
        </div>
        <div className="sonataMORETooltip__field">
          <div className="sonataMORETooltip__fieldName">
            {formatMessage({
              defaultMessage: 'Countries',
              id: 'optimizationModel.countries'
            })}
          </div>
          <div className="sonataMORETooltip__fieldValue" title={selectedModelCountries}>
            {selectedModelCountries}
          </div>
        </div>
        <div className="sonataMORETooltip__field">
          <div className="sonataMORETooltip__fieldName">
            {formatMessage({
              defaultMessage: 'Categories',
              id: 'optimizationModel.categories'
            })}
          </div>
          <div className="sonataMORETooltip__fieldValue" title={selectedModelCategories}>
            {selectedModelCategories}
          </div>
        </div>
        <div className="sonataMORETooltip__field">
          <div className="sonataMORETooltip__fieldName">
            {formatMessage({
              defaultMessage: 'Features',
              id: 'optimizationModel.variables'
            })}
          </div>
          <div className="sonataMORETooltip__fieldValue" title={selectedModelFeatures}>
            {selectedModelFeatures}
          </div>
        </div>
        <div className="sonataMORETooltip__field">
          <div className="sonataMORETooltip__fieldName">
            {formatMessage({
              defaultMessage: 'Created',
              id: 'optimizationModel.creationDate'
            })}
          </div>
          <div className="sonataMORETooltip__fieldValue">
            {optimizationModel.datasource?.created
              ? moment(optimizationModel.datasource.created).format('YYYY-MM-DD')
              : 'N/A'}
          </div>
        </div>
      </IconTooltip>
      {canViewQualityInfo && (
        <ButtonUI
          className="sonataMORETooltip__scoringInfoButton"
          onClick={openScoringInfoModal}
          shape="squareRounded"
          size="ultraSmall"
          text="Scoring Info"
          type="secondary"
        />
      )}
      {canViewQualityInfo && optimizationModel.qualityScoring && (
        <ModalUI
          closeModalCallback={closeScoringInfoModal}
          ref={scoringInfoModalRef}
          title={formatMessage({
            defaultMessage: 'Quality Scoring Info',
            id: 'optimizationModel.infoModal.title'
          })}
          width="550px"
        >
          <ScoringInfoModal qualityScoring={optimizationModel.qualityScoring} />
        </ModalUI>
      )}
    </div>
  );
}

export default React.memo(SonataMORETooltip);
